import {useMemo} from 'react';
import {
  Control,
  Controller,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {FormDataType} from '../../../types';
import {Checkbox} from '@mui/material';
import AppMUIAutocomplete from '../../../../../../../components/common/AppMUIAutoComplete';

type Props = {
  index: number;
  control: Control<FormDataType>;
  watch: UseFormWatch<FormDataType>;
  setValue: UseFormSetValue<FormDataType>;
  errors: any;
};

type SizeOption = {value: number; label: string};

const SIZE_MB_VALUES = [5, 10, 15, 20] as const;

const FILE_TYPE_IDS = [
  {id: 'word', labelKey: 'forms.dialog.fileUpload.type.word'},
  {id: 'pdf', labelKey: 'forms.dialog.fileUpload.type.pdf'},
  {id: 'ppt', labelKey: 'forms.dialog.fileUpload.type.ppt'},
  {id: 'photo', labelKey: 'forms.dialog.fileUpload.type.photo'},
  {id: 'video', labelKey: 'forms.dialog.fileUpload.type.video'},
  {id: 'audio', labelKey: 'forms.dialog.fileUpload.type.audio'},
] as const;

function UploadFile({index, control, watch, setValue, errors}: Props) {
  const {t} = useTranslation();

  const sizeOptions: SizeOption[] = useMemo(
    () =>
      SIZE_MB_VALUES.map(value => ({
        value,
        label: t('forms.dialog.fileUpload.megaBytes', {count: value}),
      })),
    [t],
  );

  const handleCheckboxChange = (value: string) => {
    const currentValues =
      watch(`questions.${index}.fileUploadConfig.allowedTypes`) || [];
    const next = currentValues.includes(value)
      ? currentValues.filter((v: string) => v !== value)
      : [...currentValues, value];
    setValue(`questions.${index}.fileUploadConfig.allowedTypes`, next, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const column1 = FILE_TYPE_IDS.slice(0, 3);
  const column2 = FILE_TYPE_IDS.slice(3, 6);

  const allowedTypesError = errors?.fileUploadConfig?.allowedTypes?.message as
    | string
    | undefined;

  return (
    <div className="flex flex-col gap-3">
      <p className="m-0 text-textColor">
        {t('forms.dialog.fileUpload.allowTypes')}
      </p>

      <Controller
        name={`questions.${index}.fileUploadConfig.allowedTypes` as any}
        control={control}
        render={({field, fieldState}) => (
          <div>
            <div className="flex flex-wrap items-center gap-5">
              <div className="flex flex-col gap-2">
                {column1.map(item => (
                  <div className="flex items-center gap-1" key={item.id}>
                    <Checkbox
                      color="secondary"
                      sx={{p: 1}}
                      value={item.id}
                      checked={
                        field.value ? field?.value?.includes(item.id) : false
                      }
                      onChange={e => handleCheckboxChange(e.target.value)}
                    />
                    <p className="m-0">{t(item.labelKey)}</p>
                  </div>
                ))}
              </div>

              <div className="flex flex-col gap-2">
                {column2.map(item => (
                  <div className="flex items-center gap-1" key={item.id}>
                    <Checkbox
                      color="secondary"
                      sx={{p: 1}}
                      value={item.id}
                      checked={
                        field.value ? field?.value?.includes(item.id) : false
                      }
                      onChange={e => handleCheckboxChange(e.target.value)}
                    />
                    <p className="m-0">{t(item.labelKey)}</p>
                  </div>
                ))}
              </div>
            </div>
            {(fieldState.error?.message || allowedTypesError) && (
              <p className="m-0 mt-1 text-[13px] text-raspberryRed">
                {t('forms.dialog.fileUpload.allowedTypesRequired')}
              </p>
            )}
          </div>
        )}
      />

      <div className="flex flex-row items-center gap-3">
        <p className="m-0 text-textColor">
          {t('forms.dialog.fileUpload.maxFileSize')}
        </p>

        <Controller
          name={`questions.${index}.fileUploadConfig.maxFileSizeMB` as any}
          control={control}
          render={({field}) => {
            const selected =
              sizeOptions.find(o => o.value === field.value) ??
              sizeOptions.find(o => o.value === 10) ??
              null;

            return (
              <div className="w-[300px]">
                <AppMUIAutocomplete<SizeOption, false, false, false>
                  size="small"
                  fullWidth
                  options={sizeOptions}
                  value={selected}
                  getOptionLabel={option => option.label}
                  isOptionEqualToValue={(a, b) => a.value === b.value}
                  onChange={(_e, val) => {
                    if (val == null) {
                      field.onChange(10);
                      return;
                    }
                    field.onChange(val.value);
                  }}
                  onBlur={field.onBlur}
                />
              </div>
            );
          }}
        />
      </div>
    </div>
  );
}

export default UploadFile;
